import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, DatePicker, Select, Spin, Tooltip } from "antd";
import SEO from "../../components/SEO";
import { DownloadOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "oidc-react";
import SharedService from "../../services/shared/service";
import { toArray } from "../../utils/array.utils";
import { convertBytes, downloadBase64File } from "../../utils/file.utils";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./Reports.css";

const Reports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userData } = useAuth();

  const [statsLoading, setStatsLoading] = useState(true);
  const [emailStatsLoading, setEmailStatsLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFile, setReportFile] = useState(null);
  const [emailReportLoading, setEmailReportLoading] = useState(false);
  const [emailReportFile, setEmailReportFile] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState([]);
  const [emailCategory, setEmailCategory] = useState([]);
  const [queueMailbox, setQueueMailbox] = useState([]);
  const [mailboxOptions, setMailboxOptions] = useState([]);
  const [source, setSource] = useState(["Email", "Upload"]);
  const [stats, setStats] = useState({
    submitted: 0,
    processed: 0,
    failed: 0,
    rejected: 0,
    duplicate: 0,
    underReview: 0,
    onHold: 0,
  });
  const [emailStats, setEmailStats] = useState({
    forReview: 0,
    inbox: 0,
    processed: 0,
    reviewedIgnore: 0,
  });

  useEffect(() => {
    const fromDate = searchParams.get("fromDate") ?? null;
    const toDate = searchParams.get("toDate") ?? null;
    const status = toArray(searchParams.get("status")) ?? null;
    const emailCategory = toArray(searchParams.get("emailCategory")) ?? null;

    setFromDate(fromDate);
    setToDate(toDate);
    setStatus(status);
    setEmailCategory(emailCategory);
    setReportFile(null);

    if (
      userData &&
      userData?.access_token &&
      userData.access_token.length !== undefined
    ) {
      fetchStats(status, fromDate, toDate, source, queueMailbox);
      fetchEmailStats(emailCategory, fromDate, toDate, queueMailbox);
      fetchMailboxOptions();
    }
  }, [userData, searchParams]);

  // When "Failed" (FailedInvoiceValidation) is selected, the API expects
  // "Received" to be included alongside it.
  const mapStatusForApi = (status = []) => {
    if (!Array.isArray(status) || !status.includes("FailedInvoiceValidation")) {
      return status;
    }
    return Array.from(new Set([...status, "Received"]));
  };

  const apiFetchDocumentStats = async ({
    status = [],
    fromDate = null,
    toDate = null,
    source = null,
    queueMailbox = null,
  }) => {
    return await SharedService.post({
      url: "documents/stats/overview",
      token: userData?.access_token,
      data: {
        status: mapStatusForApi(status),
        fromDate: fromDate,
        toDate: toDate,
        source: source,
        queueEmails: queueMailbox,
      },
    });
  };

  const fetchMailboxOptions = async () => {
    try {
      const response = await SharedService.get({
        url: "reference/mailboxes",
        token: userData?.access_token,
      });
      const options = response.map((item) => ({
        label: item.name ?? item.value,
        value: item.value,
        key: item.id,
      }));
      setMailboxOptions(options);
    } catch (error) {
      console.error("Error fetching mailbox options:", error);
    }
  };

  const apiFetchEmailStats = async ({
    emailCategory = [],
    fromDate = null,
    toDate = null,
    queueMailbox = null,
  }) => {
    return await SharedService.post({
      url: "emailValidationQueue/stats/overview",
      token: userData?.access_token,
      data: {
        folders: emailCategory,
        fromDate: fromDate,
        toDate: toDate,
        queueEmails: queueMailbox,
      },
    });
  };

  const fetchEmailStats = async (
    emailCategory,
    fromDate,
    toDate,
    queueMailbox,
  ) => {
    setEmailStatsLoading(true);
    try {
      const response = await apiFetchEmailStats({
        emailCategory,
        fromDate,
        toDate,
        queueMailbox,
      });
      setEmailStats(response);
    } catch (error) {
      console.error("Error fetching email stats:", error);
    } finally {
      setEmailStatsLoading(false);
    }
  };

  const apiFetchReportFile = async ({
    status = [],
    fromDate = null,
    toDate = null,
    source = null,
    queueMailbox = null,
  }) => {
    return await SharedService.post({
      url: "documents/report/overview",
      token: userData?.access_token,
      data: {
        status: mapStatusForApi(status),
        fromDate: fromDate,
        toDate: toDate,
        source: source,
        queueEmails: queueMailbox,
      },
    });
  };

  const fetchStats = async (
    status,
    fromDate,
    toDate,
    source,
    queueMailbox = null,
  ) => {
    setStatsLoading(true);
    try {
      const response = await apiFetchDocumentStats({
        status: status,
        fromDate: fromDate,
        toDate: toDate,
        source: source,
        queueMailbox: queueMailbox,
      });
      setStats(response);
    } catch (error) {
      console.error("Error fetching document counts:", error);
    } finally {
      setStatsLoading(false); // Stop loading after the API call finishes
    }
  };

  const fetchReportOverview = async (
    status,
    fromDate,
    toDate,
    source,
    queueMailbox = null,
  ) => {
    setReportLoading(true);
    try {
      const response = await apiFetchReportFile({
        status: status,
        fromDate: fromDate,
        toDate: toDate,
        source: source,
        queueMailbox: queueMailbox,
      });
      setReportFile(response);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
    setReportLoading(false);
  };

  const apiFetchEmailReportFile = async ({
    emailCategory = [],
    fromDate = null,
    toDate = null,
    queueMailbox = null,
  }) => {
    return await SharedService.post({
      url: "emailValidationQueue/report/overview",
      token: userData?.access_token,
      data: {
        folders: emailCategory,
        fromDate: fromDate,
        toDate: toDate,
        queueEmails: queueMailbox,
      },
    });
  };

  const fetchEmailReportOverview = async (
    emailCategory,
    fromDate,
    toDate,
    queueMailbox,
  ) => {
    setEmailReportLoading(true);
    try {
      const response = await apiFetchEmailReportFile({
        emailCategory,
        fromDate,
        toDate,
        queueMailbox,
      });
      setEmailReportFile(response);
    } catch (error) {
      console.error("Error fetching email report:", error);
    }
    setEmailReportLoading(false);
  };

  const statusOptions = [
    { label: "Submitted", value: "InvoiceCreated" },
    { label: "Processed", value: "OCRProcessed" },
    { label: "Rejected", value: "Rejected" },
    { label: "Failed", value: "FailedInvoiceValidation" },
    { label: "On Hold", value: "OnHold" },
    { label: "Duplicates", value: "Duplicate" },
    { label: "Under Review", value: "UnderReview" },
  ];

  const emailCategoryOptions = [
    { label: "For Review", value: "forReview" },
    { label: "Inbox", value: "inbox" },
    { label: "Processed", value: "processed" },
    { label: "Reviewed Ignore", value: "reviewedIgnore" },
  ];

  const updateParam = (key, value) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value && value.length > 0)
      params.set(key, Array.isArray(value) ? value.join(",") : value);
    else params.delete(key);
    setSearchParams(params, { replace: false });
  };

  const handleMailboxChange = (value) => {
    const next = value ?? [];
    setQueueMailbox(next);
    fetchStats(status, fromDate, toDate, source, next);
    fetchEmailStats(emailCategory, fromDate, toDate, next);
  };

  const handleDateChange = (dates) => {
    const params = new URLSearchParams(searchParams.toString());
    if (dates && dates[0] && dates[1]) {
      params.set("fromDate", dates[0].format("YYYY-MM-DD"));
      params.set("toDate", dates[1].format("YYYY-MM-DD"));
    } else {
      params.delete("fromDate");
      params.delete("toDate");
    }
    setSearchParams(params, { replace: false });
  };

  const pageSEOSettings = {
    title: "Reports",
  };

  const documentChartData = [
    {
      label: "Submitted",
      value: stats.submitted,
      color: "#5fc66a",
      statusKey: "InvoiceCreated",
    },
    {
      label: "Processed",
      value: stats.processed,
      color: "#3399ff",
      statusKey: "OCRProcessed",
    },
    {
      label: "Rejected",
      value: stats.rejected,
      color: "#FF6666",
      statusKey: "Rejected",
    },
    {
      label: "Failed",
      value: stats.failed,
      color: "#FF661A",
      statusKey: "FailedInvoiceValidation",
    },
    {
      label: "On Hold",
      value: stats.onHold,
      color: "#FFB347",
      statusKey: "OnHold",
    },
    {
      label: "Duplicates",
      value: stats.duplicate,
      color: "#ffd700",
      statusKey: "Duplicate",
    },
    {
      label: "Under Review",
      value: stats.underReview,
      color: "#FFA500",
      statusKey: "UnderReview",
    },
  ].filter(
    (item) => !status || status.length === 0 || status.includes(item.statusKey),
  );

  const emailChartData = [
    {
      label: "For Review",
      value: emailStats.forReview,
      color: "#ffd900",
      categoryKey: "forReview",
    },
    {
      label: "Inbox",
      value: emailStats.inbox,
      color: "#B0C4DE",
      categoryKey: "inbox",
    },
    {
      label: "Processed",
      value: emailStats.processed,
      color: "#5FC66A",
      categoryKey: "processed",
    },
    {
      label: "Reviewed Ignore",
      value: emailStats.reviewedIgnore,
      color: "#ff8080",
      categoryKey: "reviewedIgnore",
    },
  ].filter(
    (item) =>
      !emailCategory ||
      emailCategory.length === 0 ||
      emailCategory.includes(item.categoryKey),
  );

  const documentTotal = documentChartData.reduce(
    (sum, d) => sum + (d.value || 0),
    0,
  );
  const emailTotal = emailChartData.reduce((sum, d) => sum + (d.value || 0), 0);

  const shortFileType = (file) => {
    const fromName = file?.filename?.split(".").pop();
    if (fromName && fromName.length <= 5) return fromName.toUpperCase();
    const fromMime = file?.filetype?.split("/").pop()?.split(".").pop();
    if (fromMime?.includes("spreadsheet")) return "XLSX";
    if (fromMime?.includes("wordprocessing")) return "DOCX";
    if (fromMime?.includes("pdf")) return "PDF";
    return (fromMime || "FILE").slice(0, 4).toUpperCase();
  };

  const fileTile = (file, onDownload) => {
    const ext = shortFileType(file);
    return (
      <div className="mt-4 p-3 bg-[#F7F8FB] rounded-lg flex items-center gap-3 border border-[#E4E7EE]">
        <div className="w-10 h-10 rounded-md bg-[#FDECEC] text-[#A4231D] flex items-center justify-center text-[11px] font-bold tracking-wide">
          {ext}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#020A08] truncate">
            {file?.filename}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {ext} · {convertBytes(file?.filesize, "MB")} MB
          </div>
        </div>
        <Button size="small" icon={<DownloadOutlined />} onClick={onDownload}>
          Download
        </Button>
      </div>
    );
  };

  return (
    <div>
      <SEO {...pageSEOSettings} />

      {/* Page header */}
      <div className="px-4 sm:px-6 py-5 bg-white border-b-2 border-b-[#CDD6DB] flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[#020A08] text-[28px] font-semibold leading-tight">
            Reports
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Generate processing and email statistics, download as PDF or Excel.
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Select
            mode="multiple"
            allowClear
            placeholder="All mailboxes"
            options={mailboxOptions}
            value={queueMailbox?.length > 0 ? queueMailbox : undefined}
            onChange={handleMailboxChange}
            className="min-w-[220px] sm:max-w-[320px]"
            maxTagCount="responsive"
          />
          <DatePicker.RangePicker
            value={fromDate && toDate ? [dayjs(fromDate), dayjs(toDate)] : null}
            onChange={handleDateChange}
            allowClear
            className="min-w-[260px]"
          />
        </div>
      </div>

      <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        {/* Document stats */}
        <Spin spinning={statsLoading} className="custom-spin">
          <div className="bg-white rounded-lg border border-[#E4E7EE] h-full flex flex-col">
            <div className="px-5 py-4 border-b border-[#F1F3F7] flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-[#020A08]">
                  Document stats
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Invoice volume by processing status ·{" "}
                  {documentTotal.toLocaleString()} total
                </div>
              </div>
              <Tooltip title="Generate report">
                <Button
                  size="small"
                  icon={<SyncOutlined spin={reportLoading} />}
                  onClick={() =>
                    fetchReportOverview(status, fromDate, toDate, source)
                  }
                  disabled={reportLoading}
                >
                  Generate
                </Button>
              </Tooltip>
              <Tooltip
                title={reportFile ? "Download report" : "No report available"}
              >
                <Button
                  size="small"
                  type="primary"
                  icon={<DownloadOutlined />}
                  disabled={!reportFile}
                  onClick={() =>
                    reportFile &&
                    downloadBase64File({
                      content: reportFile?.base64File,
                      fileName: reportFile?.filename,
                      type: reportFile?.filetype,
                    })
                  }
                >
                  Download
                </Button>
              </Tooltip>
            </div>

            <div className="p-5 flex-1 flex flex-col">
              <Select
                mode="multiple"
                allowClear
                placeholder="Filter by status"
                options={statusOptions}
                value={status?.length > 0 ? status : undefined}
                onChange={(val) => updateParam("status", val)}
                className="w-full mb-4"
                maxTagCount="responsive"
              />

              <div
                style={{
                  height: Math.max(documentChartData.length * 42 + 30, 280),
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={documentChartData}
                    layout="vertical"
                    margin={{ top: 10, right: 50, left: 8, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      stroke="#e8e8e8"
                    />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#7A849A" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={{ fontSize: 12, fill: "#374152" }}
                      axisLine={false}
                      tickLine={false}
                      width={120}
                    />
                    <RechartsTooltip
                      cursor={{ fill: "rgba(0,0,0,0.04)" }}
                      formatter={(value, name, props) => [
                        value.toLocaleString(),
                        props.payload.label,
                      ]}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={22}>
                      <LabelList
                        dataKey="value"
                        position="right"
                        formatter={(v) => v.toLocaleString()}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          fill: "#1B2332",
                        }}
                      />
                      {documentChartData.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {reportFile &&
                fileTile(reportFile, () =>
                  downloadBase64File({
                    content: reportFile?.base64File,
                    fileName: reportFile?.filename,
                    type: reportFile?.filetype,
                  }),
                )}
            </div>
          </div>
        </Spin>

        {/* Email stats */}
        <Spin spinning={emailStatsLoading} className="custom-spin">
          <div className="bg-white rounded-lg border border-[#E4E7EE] h-full flex flex-col">
            <div className="px-5 py-4 border-b border-[#F1F3F7] flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-[#020A08]">
                  Email stats
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Emails received per queue category ·{" "}
                  {emailTotal.toLocaleString()} total
                </div>
              </div>
              <Tooltip title="Generate report">
                <Button
                  size="small"
                  icon={<SyncOutlined spin={emailReportLoading} />}
                  onClick={() =>
                    fetchEmailReportOverview(
                      emailCategory,
                      fromDate,
                      toDate,
                      queueMailbox,
                    )
                  }
                  disabled={emailReportLoading}
                >
                  Generate
                </Button>
              </Tooltip>
              <Tooltip
                title={
                  emailReportFile ? "Download report" : "No report available"
                }
              >
                <Button
                  size="small"
                  type="primary"
                  icon={<DownloadOutlined />}
                  disabled={!emailReportFile}
                  onClick={() =>
                    emailReportFile &&
                    downloadBase64File({
                      content: emailReportFile?.base64File,
                      fileName: emailReportFile?.filename,
                      type: emailReportFile?.filetype,
                    })
                  }
                >
                  Download
                </Button>
              </Tooltip>
            </div>

            <div className="p-5 flex-1 flex flex-col">
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All categories"
                  options={emailCategoryOptions}
                  value={emailCategory?.length > 0 ? emailCategory : undefined}
                  onChange={(val) => updateParam("emailCategory", val)}
                  className="flex-1 min-w-0"
                  maxTagCount="responsive"
                />
              </div>

              <div className="flex-1 flex flex-col sm:flex-row items-center gap-6 min-h-[240px]">
                <div className="relative" style={{ width: 220, height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={
                          emailChartData.length > 0
                            ? emailChartData
                            : [{ label: "No data", value: 1, color: "#E4E7EE" }]
                        }
                        dataKey="value"
                        nameKey="label"
                        innerRadius={65}
                        outerRadius={95}
                        paddingAngle={emailTotal > 0 ? 2 : 0}
                        stroke="none"
                      >
                        {(emailChartData.length > 0
                          ? emailChartData
                          : [{ color: "#E4E7EE" }]
                        ).map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name, props) => [
                          value.toLocaleString(),
                          props.payload.label,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-[26px] font-bold text-[#0B1220] leading-none">
                      {emailTotal.toLocaleString()}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">Emails</div>
                  </div>
                </div>

                <div className="flex-1 w-full flex flex-col gap-2.5">
                  {emailChartData.map((d) => {
                    const pct =
                      emailTotal > 0
                        ? Math.round((d.value / emailTotal) * 100)
                        : 0;
                    return (
                      <div
                        key={d.label}
                        className="flex items-center gap-2 text-[13px]"
                      >
                        <span
                          className="w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: d.color }}
                        />
                        <span className="flex-1 text-[#1B2332]">{d.label}</span>
                        <span className="font-semibold text-[#0B1220] tabular-nums">
                          {d.value.toLocaleString()}
                        </span>
                        <span className="text-gray-500 w-10 text-right tabular-nums">
                          {pct}%
                        </span>
                      </div>
                    );
                  })}
                  {emailChartData.length === 0 && (
                    <div className="text-sm text-gray-400">
                      No data for the selected filters.
                    </div>
                  )}
                </div>
              </div>

              {emailReportFile &&
                fileTile(emailReportFile, () =>
                  downloadBase64File({
                    content: emailReportFile?.base64File,
                    fileName: emailReportFile?.filename,
                    type: emailReportFile?.filetype,
                  }),
                )}
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default Reports;
